import { useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
    addMessage,
    setIsLoading,
    updateMessage,
    setError,
    setReasoning,
    Message,
    initialiseMessageThread,
    setIsLoadingChatHistory,
    setGlobalSuggestedQuestions,
    setAskAicaRightPanelMode,
    ReasoningStep,
    resetReasoning,
    setIsClearingChat,
    setLibraryLoading,
    setLibraryFiles,
    addLibraryFiles,
    LibraryFile,
    AgentInput,
    setConversations,
    setLoadingConversations,
    resetChatView,
    setSelectedConversation,
} from './actions';
import { clearMemory, getAskAicaFiles, getChatHistory, getOrgConversations, streamAskAicaChat, GeneratedFileAttachment } from './apiHelper';

// Maps a just-generated file's attachment metadata (already present in the chat
// completion response) straight into a LibraryFile — avoids a redundant
// getAskAicaFiles round-trip on every generation. createdAt isn't part of this
// payload (only the Library listing endpoint has it), so we use "now"; the next
// real fetch (e.g. panel reopen) overwrites it with the authoritative value.
const mapAttachmentToLibraryFile = (attachment: GeneratedFileAttachment): LibraryFile => ({
    fileUrl: attachment.file_url,
    fileName: attachment.file_name,
    fileType: attachment.file_type,
    createdAt: new Date().toISOString(),
    source: attachment.source || '',
    mnemosyneDocId: attachment.mnemosyne_doc_id || '',
});

export const useAskAica = () => {
    const streamStartedCallbackRef = useRef<((value: boolean) => void) | null>(null);
    const dispatch = useAppDispatch();
    const user_id = useAppSelector((state) => state.user.userData.userId);
    const user_name = useAppSelector((state) => state.user.userData.userName || undefined);
    const params = useParams();
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const { messages, reasoning, isLoading, error, uiState, pagination, isLoadingChatHistory, selectedConversationId } = useAppSelector(
        (state) => state.dashboard.askAicaReducer,
    );
    const { selectedSources } = uiState;
    const org_id = params.id ? params.id : investorOrgId;
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const fetchChatHistory = useCallback(
        // conversationId?: string | null
        //   undefined  → use selectedConversationId from the store (load-more falls through here)
        //   null       → force no conversation id (back-to-own escape hatch — see ConversationSidebar)
        //   string     → explicit id (row-click passes the id directly)
        (isInitialFetch = false, conversationId?: string | null, reset = false) => {
            const effectiveConversationId = conversationId === null ? undefined : conversationId ?? selectedConversationId ?? undefined;
            // On a conversation switch (reset=true), always fetch page 1 so we
            // don't close over a stale pagination.page from the previous chat.
            const page = reset ? 1 : pagination.page;
            dispatch(setIsLoadingChatHistory(true));
            getChatHistory(user_id, org_id, page, pagination.pageSize, effectiveConversationId)
                .then((data) => {
                    let messageThread: Message[] = [];
                    if (data && Array.isArray(data.items)) {
                        data.items.reverse().forEach((item: { user_message?: Message; assistant_message?: Message }) => {
                            if (item.assistant_message) {
                                const isFeedbackAck = !!(item.assistant_message.metadata as any)?.feedback_ack;
                                messageThread.push({
                                    id: item.assistant_message.id,
                                    type: item.assistant_message.type,
                                    timestamp: new Date(item.assistant_message.created_at as string),
                                    content: item.assistant_message.input_text as string,
                                    status: item.assistant_message.status as string,
                                    // Generated-file metadata (one-pager DOCX) — lets the
                                    // FileAttachmentCard render from history after refresh
                                    metadata: item.assistant_message.metadata,
                                    isFeedbackAck,
                                });
                            }
                            if (item.user_message) {
                                const isFeedback = item.user_message.query_source === 'feedback';
                                messageThread.push({
                                    id: item.user_message.id,
                                    type: item.user_message.type,
                                    timestamp: new Date(item.user_message.created_at as string),
                                    content: item.user_message.input_text as string,
                                    status: item.user_message.status as string,
                                    // Map query_source='feedback' to flowKey so MessagesContainer
                                    // renders the distinct feedback bubble on reload (A-17).
                                    flowKey: isFeedback ? 'feedback' : undefined,
                                });
                            }
                        });

                        // If there are suggestions from chat history, set them as global suggestions
                        if (data.suggestions && Array.isArray(data.suggestions) && data.suggestions.length > 0) {
                            dispatch(setGlobalSuggestedQuestions(data.suggestions));
                        }

                        dispatch(initialiseMessageThread(messageThread));
                    }
                })
                .catch((err) => {
                    console.error('Error fetching chat history:', err);
                    toast.error('Failed to fetch chat history');
                    dispatch(setIsLoadingChatHistory(false));
                });
        },
        [user_id, org_id, pagination, selectedConversationId],
    );

    // Fetch the Library file list for an org (Polus BEFE), via getAskAicaFiles's
    // onSuccess/onError handlers rather than a .then()/.catch() chain. Dispatching
    // setLibraryFiles on both paths (mapped files, or [] on error) also resets
    // isLoading in one place — including when onSuccess's own mapping throws,
    // since getAskAicaFiles routes that to onError too.
    const fetchLibraryFiles = useCallback(
        (orgId: string) => {
            dispatch(setLibraryLoading(true));
            getAskAicaFiles(orgId, {
                onSuccess: (files: any[]) => {
                    const libraryFiles: LibraryFile[] = files.map((file) => ({
                        fileUrl: file.file_url,
                        fileName: file.file_name,
                        fileType: file.file_type,
                        createdAt: file.created_at,
                        source: file.source,
                        mnemosyneDocId: file.mnemosyne_doc_id,
                    }));
                    dispatch(setLibraryFiles(libraryFiles));
                },
                onError: () => {
                    dispatch(setLibraryFiles([]));
                },
            });
        },
        [dispatch],
    );

    const fetchOrgConversations = useCallback(() => {
        if (!user_id || !org_id) return;
        // Capture the orgId at request time. If the user switches company before
        // the response arrives, skip the dispatch to prevent cross-tenant leakage.
        const requestedOrgId = org_id;
        dispatch(setLoadingConversations(true));
        getOrgConversations(user_id, org_id, {
            onSuccess: (data) => {
                if (requestedOrgId !== org_id) return;
                dispatch(setConversations(data?.conversations ?? []));
            },
            onError: (err) => {
                console.error('Error fetching org conversations:', err);
                dispatch(setLoadingConversations(false));
            },
        });
    }, [user_id, org_id, dispatch]);

    // Function to send a message and get a streaming response
    const sendMessage = useCallback(
        (content: string, flowKey?: string, agentInput?: AgentInput) => {
            if (!content.trim() || isLoading) return;

            // Reset stream started state when sending a new message
            if (streamStartedCallbackRef.current) {
                streamStartedCallbackRef.current(false);
            }

            // Create and add user message
            const userMessage = {
                id: Date.now(),
                type: 'user' as const,
                content,
                timestamp: new Date(),
                sources: selectedSources,
                flowKey,
                agentInput,
            };
            dispatch(addMessage(userMessage));

            // Create placeholder for AI response
            const aiMessageId = Date.now() + 1;
            const aiMessage = {
                id: aiMessageId,
                type: 'assistant' as const,
                content: '',
                timestamp: new Date(),
            };
            dispatch(addMessage(aiMessage));
            dispatch(setIsLoading(true));
            dispatch(resetReasoning());
            dispatch(setAskAicaRightPanelMode('reasoning'));

            // Format messages for API
            const apiMessages = [...messages]
                .reverse()
                .concat(userMessage)
                .slice(-3)
                .map((msg) => ({
                    role: msg.type === 'user' ? 'user' : 'assistant',
                    content: msg.content,
                }));

            // Abort controller for cleaning up the stream
            let abortStream: () => void;

            try {
                // Stream the response
                abortStream = streamAskAicaChat(
                    apiMessages,
                    selectedSources,
                    user_id,
                    org_id,
                    // On each chunk
                    (chunk, suggestedQuestions, reasoning, hasNewReasoning, isFirst, metadata) => {
                        // Update the message content
                        dispatch(updateMessage(aiMessageId, chunk, metadata || {}));

                        // If this is actual content (not just a step_type update), notify that streaming has started
                        if (chunk && streamStartedCallbackRef.current) {
                            streamStartedCallbackRef.current(true);
                        }

                        // Handle suggested questions if present
                        if (suggestedQuestions && suggestedQuestions.length > 0) {
                            dispatch(setGlobalSuggestedQuestions(suggestedQuestions));
                        }

                        // Handle reasoning if present
                        dispatch(setReasoning(aiMessageId, reasoning as ReasoningStep[], hasNewReasoning));
                    },
                    // On complete
                    (response) => {
                        dispatch(setIsLoading(false));
                        if (response.metadata) {
                            dispatch(updateMessage(aiMessageId, '', response.metadata));
                        }

                        // Refresh the sidebar so a new conversation title appears
                        // in "Your Conversations" without waiting for the next poll.
                        fetchOrgConversations();

                        // A completed message that generated a file (e.g. a one-pager) already
                        // carries the full per-file detail needed for the Library list — add it
                        // directly instead of refetching. Only files that were durably persisted
                        // (mnemosyne_doc_id set) are added, since only those ever appear in a
                        // real fetch — an ephemeral/upload-failed file would never match on refetch.
                        const attachments = response.metadata?.attachments;
                        if (attachments?.length) {
                            const durableFiles = attachments
                                .filter((attachment) => !!attachment.mnemosyne_doc_id)
                                .map(mapAttachmentToLibraryFile);
                            if (durableFiles.length > 0) dispatch(addLibraryFiles(durableFiles));
                        } else if (response.metadata?.file_url) {
                            // Defensive fallback for a metadata shape without per-file detail —
                            // refetch so the Library list still reflects the new file.
                            fetchLibraryFiles(org_id);
                        }

                        // Set final suggested questions if any
                        if (response.suggestedQuestions && response.suggestedQuestions.length > 0) {
                            dispatch(setGlobalSuggestedQuestions(response.suggestedQuestions));
                        }

                        // Set final reasoning if any
                        dispatch(setReasoning(aiMessageId, response.reasoning as ReasoningStep[], response.hasNewReasoning));

                        // Set right panel mode to suggestions after 2 seconds
                        setTimeout(() => {
                            dispatch(setAskAicaRightPanelMode('suggestions'));
                        }, 2000);
                    },
                    // On error
                    (error) => {
                        console.error('Error in streamAskAicaChat:', error);
                        dispatch(setIsLoading(false));
                        dispatch(setError(error.message || 'Failed to get response'));
                    },
                    flowKey,
                    agentInput,
                    user_name,
                );
            } catch (error: any) {
                dispatch(setIsLoading(false));
                dispatch(setError(error.message || 'Failed to send message'));
            }

            // Return cleanup function
            return () => {
                if (abortStream) abortStream();
            };
        },
        [dispatch, isLoading, messages, selectedSources, fetchLibraryFiles, fetchOrgConversations],
    );

    const clearChatMemory = useCallback(() => {
        // Optimistic: empty the center immediately so the user sees a clean slate.
        dispatch(resetChatView());
        dispatch(setIsClearingChat(true));
        clearMemory(user_id, org_id)
            .then(() => {
                toast.success('Chat memory cleared successfully');
                dispatch(setIsClearingChat(false));
                // The cleared conversation is soft-deleted — drop it from the sidebar
                // and return state to the own/active default.
                dispatch(setSelectedConversation(null));
                fetchOrgConversations();
            })
            .catch((err) => {
                console.error('Error clearing chat:', err);
                dispatch(setIsClearingChat(false));
                toast.error('Failed to clear chat memory');
                // Resync sidebar in case the clear partially succeeded.
                fetchOrgConversations();
            });
    }, [dispatch, user_id, org_id, fetchOrgConversations]);

    const registerStreamStartedCallback = (callback: (value: boolean) => void) => {
        streamStartedCallbackRef.current = callback;
    };

    return {
        messages,
        reasoning,
        isLoading,
        error,
        sendMessage,
        clearChatMemory,
        registerStreamStartedCallback,
        isLoadingChatHistory,
        messagesEndRef,
        fetchChatHistory,
        fetchLibraryFiles,
        fetchOrgConversations,
        hasMoreData: pagination.hasMoreData,
    };
};

export default useAskAica;
